import { io } from 'socket.io-client';

class SocketIOService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.isConnected = false;
    
    // Build WebSocket URL from VITE_API_URL
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    const url = new URL(apiUrl);
    this.wsUrl = url.origin;

    
    // Auto-connect on creation
    this.connect();
  }

  connect() {
    // If socket already exists and is connected, ensure listeners are set up
    if (this.socket?.connected) {
      console.log('🔌 [SocketService] Socket already connected, ensuring listeners are set up');
      // Always ensure listeners are set up (safe to call multiple times due to .off() before .on())
      this.setupSocketListeners();
      return;
    }

    // Create new socket connection
    this.socket = io(this.wsUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    this.setupSocketListeners();
  }

  setupSocketListeners() {
    if (!this.socket) {
      console.warn('⚠️ [SocketService] Cannot setup listeners - no socket');
      return;
    }

    // console.log('🔧 [SocketService] Setting up socket listeners, socket connected:', this.socket.connected);

    // Remove existing listeners to avoid duplicates, then set up fresh ones
    this.socket.off('connect');
    this.socket.on('connect', () => {
      // console.log('🟢 [Socket.IO] Connected with socket ID:', this.socket.id);
      this.isConnected = true;
      
      // Register this socket with backend for targeted messaging
      this.socket.emit('register_socket', { socket_id: this.socket.id });
      // console.log('📝 [Socket.IO] Socket ID registered with backend');

      // Join the per-socket room to receive targeted messages
      this.socket.emit('join_room', { room: `socket_${this.socket.id}` });
      // console.log(`📋 [Socket.IO] Joined per-socket room: socket_${this.socket.id}`);
    });

    // If socket is already connected, register and join room immediately
    if (this.socket.connected && this.socket.id) {
      // console.log('🔧 [SocketService] Socket already connected, registering immediately');
      this.socket.emit('register_socket', { socket_id: this.socket.id });
      this.socket.emit('join_room', { room: `socket_${this.socket.id}` });
      // console.log(`📋 [Socket.IO] Re-joined per-socket room: socket_${this.socket.id}`);
    }

    this.socket.off('disconnect');
    this.socket.on('disconnect', () => {
      this.isConnected = false;
    });

    this.socket.off('connect_error');
    this.socket.on('connect_error', (error) => {
    });

    // Listen for server messages (sent directly to this socket)
    // IMPORTANT: Always attach handler to current socket, even if handler exists
    // This ensures handler is attached after socket reconnection
    if (!this._progressUpdateHandler) {
      this._progressUpdateHandler = (data) => {
        this.notifyListeners('progress_update', data);
      };
    }
    // Always attach to current socket (Socket.IO handles duplicates safely)
    this.socket.on('progress_update', this._progressUpdateHandler);

    this.socket.on('progress_complete', (data) => {
      this.notifyListeners('progress_complete', data);
    });

    this.socket.on('extraction_results', (data) => {
      this.notifyListeners('extraction_results', data);
    });

    this.socket.on('connected', (data) => {
    });

    // Handle any other events (but don't log them to avoid console spam)
    this.socket.onAny((eventName, ...args) => {
      this.notifyListeners(eventName, args.length === 1 ? args[0] : args);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  subscribe(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
    // console.log(`📝 [SocketService] Subscribed to ${eventType}, total listeners: ${this.listeners.get(eventType).size}`);
  }

  unsubscribe(eventType, callback) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).delete(callback);
    }
  }

  joinRoom(room) {
    if (!room) {
      console.warn('⚠️ [Socket.IO] joinRoom called without room name');
      return;
    }

    const emitJoin = () => {
      if (this.socket) {
        console.log(`📋 [Socket.IO] Joining room: ${room}`);
        this.socket.emit('join_room', { room });
      }
    };

    if (this.socket?.connected) {
      emitJoin();
    } else {
      this.connect();
      this.socket?.once('connect', emitJoin);
    }
  }

  notifyListeners(eventType, payload) {
    if (this.listeners.has(eventType)) {
      const listenerCount = this.listeners.get(eventType).size;
      console.log(`📢 [SocketService] Notifying ${listenerCount} listener(s) for ${eventType}`);
      
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`❌ [SocketService] Error in listener for ${eventType}:`, error);
        }
      });
    } else {
      // console.warn(`⚠️ [SocketService] No listeners registered for ${eventType}`);
    }
  }

  send(eventName, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(eventName, data);
    } else {
    }
  }

  isReady() {
    return this.isConnected && this.socket?.connected;
  }

  // For backward compatibility - return the socket ID
  async waitForConnection(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (this.isConnected) {
        resolve();
        return;
      }

      const timeoutId = setTimeout(() => {
        reject(new Error(`Socket.IO connection timeout after ${timeout}ms`));
      }, timeout);

      this.socket.on('connect', () => {
        clearTimeout(timeoutId);
        resolve();
      });
    });
  }

  getSocketId() {
    return this.socket?.id || null;
  }

  // Keep this for backward compatibility with old code
  getSessionId() {
    return this.socket?.id || null;
  }
}

// Create and export singleton instance
const socketService = new SocketIOService();

export default socketService;
